import { useEffect, useState } from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { validateAuthToken } from "@/guards/validateAuthToken";
import { validateActiveUser } from "@/guards/validateActiveUser";

import { columns } from "@/components/patients/invites/table/columns";
import { DataTable } from "@/components/patients/invites/table/DataTable";
import { SectionRoute } from "@/components/shared/SectionRoute";
import { HeadingOne } from "@/components/ui/typography/HeadingOne";
import { useAppDispatch, useAppSelector } from "@/app/store";
import { getConnections } from "@/app/store/connection/connectionSlice";
import { IInvitationsData } from "@/components/patients/invites/table/types";
import { SkeletonTable } from "@/components/shared/skeletons/SkeletonTable";

export const Route = createFileRoute("/dashboard/patients/invites")({
  beforeLoad: () => {
    validateAuthToken();
    validateActiveUser();
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { connectionList, status } = useAppSelector(
    (state) => state.connection
  );
  const [hasRequestedConnections, setHasRequestedConnections] = useState(false);

  useEffect(() => {
    setHasRequestedConnections(true);
    dispatch(getConnections());
  }, [dispatch]);

  const invitationsData: IInvitationsData[] = connectionList
    .filter((connection) => connection?.patientInfo)
    .map((connection) => ({
      id: connection.id,
      firstname: connection.patientInfo.firstname,
      surnames: connection.patientInfo.surnames,
      email: connection.patientInfo.email,
      date: connection.createdAt.toString(),
      status: connection.currentStatus,
    }));

  if (status === "failed" && hasRequestedConnections) {
    return <Navigate to="/dashboard/error/generic-error" />;
  }

  if (status === "idle" || status === "loading") {
    return (
      <>
        <SectionRoute
          route_one={t("routes.dashboard")}
          route_two={t("routes.patients")}
          route_two_link="/dashboard/patients"
          route_three={t("routes.invites")}
        />
        <SkeletonTable />
      </>
    );
  }

  return (
    <div className="pb-8">
      <SectionRoute
        route_one={t("routes.dashboard")}
        route_two={t("routes.patients")}
        route_two_link="/dashboard/patients"
        route_three={t("routes.invites")}
      />
      <div className="mb-8 max-w-7xl mx-auto">
        <div className="my-6">
          <HeadingOne>{t("invites.title")}</HeadingOne>
        </div>
        <div>
          <DataTable columns={columns} data={invitationsData} />
        </div>
      </div>
    </div>
  );
}
